// Helfer fuer die Aufloesung von Building/Floor/Room aus der Tour.
// Bewusst pure Funktionen, ohne Seiteneffekte und ohne State-Kopplung.

import type { Building, Floor, Room, Tour } from '@/types/tour';

export function getBuilding(tour: Tour, buildingId: string): Building | undefined {
  return tour.buildings.find((b) => b.id === buildingId);
}

export function getFloor(
  tour: Tour,
  buildingId: string,
  floorId: string,
): Floor | undefined {
  return getBuilding(tour, buildingId)?.floors.find((f) => f.id === floorId);
}

export function getRoom(
  tour: Tour,
  buildingId: string,
  floorId: string,
  roomId: string,
): Room | undefined {
  return getFloor(tour, buildingId, floorId)?.rooms.find((r) => r.id === roomId);
}

// Findet das (Building, Floor, Room)-Triple zu einer Room-ID. Wird vom
// Floorplan-Click-Handler genutzt, weil ein Hotspot nur die Room-ID kennt.
export interface RoomLocation {
  building: Building;
  floor: Floor;
  room: Room;
}

export function findRoomLocation(tour: Tour, roomId: string): RoomLocation | undefined {
  for (const building of tour.buildings) {
    for (const floor of building.floors) {
      const room = floor.rooms.find((r) => r.id === roomId);
      if (room) return { building, floor, room };
    }
  }
  return undefined;
}

export function firstRoomLocation(tour: Tour): RoomLocation | undefined {
  const building = tour.buildings[0];
  const floor = building?.floors[0];
  const room = floor?.rooms[0];
  if (building && floor && room) return { building, floor, room };
  return undefined;
}

// Liefert den Text, der auf dem Floorplan unter einem Hotspot stehen
// soll. Reihenfolge: explizites Hotspot-Label > Raum-Name (gekuerzt) >
// Auto-Nummer ("01", "02", ...) basierend auf dem Index in der Liste.
export function hotspotDisplayLabel(
  hotspot: { label?: string },
  room: { name?: string } | undefined,
  index: number,
): string {
  const explicit = hotspot.label?.trim();
  if (explicit) return explicit;
  const name = room?.name?.trim();
  if (name) return name.length > 16 ? name.slice(0, 14) + '…' : name;
  return String(index + 1).padStart(2, '0');
}
